
// Image Quality Analysis Types and Functions
export type KPIResult = {
  name: string;
  value: number;
  unit?: string;
  verdict: "pass" | "warn" | "fail";
  details?: string;
};

export type QualityReport = {
  width: number;
  height: number;
  kpis: KPIResult[];
  score: number; // 0..100
  summary: "good" | "ok" | "bad";
};

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

function drawImageToCanvas(file: File): Promise<{ img: ImageData; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('Canvas 2D not supported'));
      return;
    }

    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, img.width, img.height);
      resolve({ img: imageData, width: img.width, height: img.height });
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
}

function toLuma(src: ImageData) {
  const { data, width, height } = src;
  const Y = new Float32Array(width * height);
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    // Rec. 709 luma
    Y[p] = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
  }
  return { Y, width, height };
}

function convolve3x3(Y: Float32Array, width: number, height: number, K: number[]) {
  const out = new Float32Array(width * height);
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      let acc = 0;
      let i = y * width + x;
      acc += K[0] * Y[i - width - 1];
      acc += K[1] * Y[i - width];
      acc += K[2] * Y[i - width + 1];
      acc += K[3] * Y[i - 1];
      acc += K[4] * Y[i];
      acc += K[5] * Y[i + 1];
      acc += K[6] * Y[i + width - 1];
      acc += K[7] * Y[i + width];
      acc += K[8] * Y[i + width + 1];
      out[i] = acc;
    }
  }
  return out;
}

function variance(arr: Float32Array) {
  let mean = 0, m2 = 0, n = 0;
  for (let i = 0; i < arr.length; i++) {
    const x = arr[i];
    if (!Number.isFinite(x)) continue;
    n++;
    const d = x - mean;
    mean += d / n;
    m2 += d * (x - mean);
  }
  return m2 / Math.max(1, n - 1);
}

function percentWhere(Y: Float32Array, predicate: (v: number) => boolean) {
  let c = 0;
  for (let i = 0; i < Y.length; i++) if (predicate(Y[i])) c++;
  return (100 * c) / Y.length;
}

function blockStats(Y: Float32Array, width: number, height: number, bs = 8) {
  const stats: { mean: number; variance: number }[] = [];
  for (let by = 0; by < height; by += bs) {
    for (let bx = 0; bx < width; bx += bs) {
      let sum = 0, sum2 = 0, n = 0;
      for (let y = by; y < Math.min(by + bs, height); y++) {
        for (let x = bx; x < Math.min(bx + bs, width); x++) {
          const v = Y[y * width + x];
          sum += v; sum2 += v * v; n++;
        }
      }
      const mean = sum / Math.max(1, n);
      const variance = clamp(sum2 / Math.max(1, n) - mean * mean, 0, 1e9);
      stats.push({ mean, variance });
    }
  }
  return stats;
}

function kpiSharpnessVarianceOfLaplacian(Y: Float32Array, width: number, height: number): KPIResult {
  const lap = convolve3x3(Y, width, height, [
    0,  1, 0,
    1, -4, 1,
    0,  1, 0,
  ]);
  const v = variance(lap);
  const pass = 130;
  const warn = 80;
  const verdict = v >= pass ? "pass" : v >= warn ? "warn" : "fail";
  return { name: "Sharpness (Var(Laplacian))", value: v, unit: "au", verdict, details: "Higher is sharper" };
}

function kpiEdgeDensity(Y: Float32Array, width: number, height: number): KPIResult {
  const gx = convolve3x3(Y, width, height, [ -1,0,1, -2,0,2, -1,0,1 ]);
  const gy = convolve3x3(Y, width, height, [ -1,-2,-1, 0,0,0, 1,2,1 ]);
  let strong = 0; const N = Y.length;
  for (let i = 0; i < N; i++) {
    const mag = Math.hypot(gx[i] || 0, gy[i] || 0);
    if (mag > 20) strong++;
  }
  const pct = (100 * strong) / N;
  const verdict = pct >= 5 ? "pass" : pct >= 2.5 ? "warn" : "fail";
  return { name: "Edge density (>)", value: pct, unit: "%", verdict, details: ">20 Sobel magnitude" };
}

function kpiBlurLikelihood(lapVar: number): KPIResult {
  const scaled = 100 * (1 - Math.tanh(lapVar / 150));
  const verdict = scaled <= 30 ? "pass" : scaled <= 55 ? "warn" : "fail";
  return { name: "Blur likelihood", value: scaled, unit: "%", verdict, details: "Lower is better" };
}

function kpiFocusAtCenter(Y: Float32Array, width: number, height: number): KPIResult {
  const cx0 = Math.floor(width * 0.3);
  const cx1 = Math.floor(width * 0.7);
  const cy0 = Math.floor(height * 0.3);
  const cy1 = Math.floor(height * 0.7);
  const centerMask = new Uint8Array(width * height);
  for (let y = cy0; y < cy1; y++) for (let x = cx0; x < cx1; x++) centerMask[y*width+x] = 1;

  const lap = convolve3x3(Y, width, height, [0,1,0,1,-4,1,0,1,0]);
  let sumC = 0, nC = 0, sumAll = 0, nAll = 0;
  for (let i = 0; i < lap.length; i++) {
    const v = Math.abs(lap[i]);
    sumAll += v; nAll++;
    if (centerMask[i]) { sumC += v; nC++; }
  }
  const ratio = (sumC / Math.max(1, nC)) / (sumAll / Math.max(1, nAll));
  const verdict = ratio >= 1.0 ? "pass" : ratio >= 0.8 ? "warn" : "fail";
  return { name: "Focus @ center (ratio)", value: ratio, unit: "x", verdict, details: ">=1 means center is sharp" };
}

function kpiExposure(Y: Float32Array): KPIResult[] {
  const mean = Y.reduce((a, b) => a + b, 0) / Math.max(1, Y.length);
  const underPct = percentWhere(Y, v => v < 20);
  const overPct = percentWhere(Y, v => v > 235);
  const verdictMean = mean >= 60 && mean <= 190 ? "pass" : (mean >= 45 && mean <= 210 ? "warn" : "fail");
  const verdictClip = (underPct < 5 && overPct < 5) ? "pass" : ((underPct < 10 && overPct < 10) ? "warn" : "fail");
  return [
    { name: "Exposure (mean Y)", value: mean, unit: "0-255", verdict: verdictMean, details: "Target ~110-150" },
    { name: "Clipping (dark)", value: underPct, unit: "%", verdict: verdictClip, details: "% of Y<20" },
    { name: "Clipping (bright)", value: overPct, unit: "%", verdict: verdictClip, details: "% of Y>235" },
  ];
}

function kpiContrast(Y: Float32Array): KPIResult {
  let mean = 0; for (let i = 0; i < Y.length; i++) mean += Y[i];
  mean /= Math.max(1, Y.length);
  let s2 = 0; for (let i = 0; i < Y.length; i++) { const d = Y[i] - mean; s2 += d * d; }
  const rms = Math.sqrt(s2 / Math.max(1, Y.length));
  const verdict = rms >= 35 ? "pass" : rms >= 22 ? "warn" : "fail";
  return { name: "Contrast (RMS Y)", value: rms, unit: "au", verdict };
}

function kpiNoise(Y: Float32Array, width: number, height: number): KPIResult {
  const stats = blockStats(Y, width, height, 8);
  stats.sort((a, b) => a.variance - b.variance);
  const k = Math.max(1, Math.floor(stats.length * 0.1));
  let sumVar = 0; for (let i = 0; i < k; i++) sumVar += stats[i].variance;
  const sigma = Math.sqrt(sumVar / k);
  const verdict = sigma < 6 ? "pass" : sigma < 10 ? "warn" : "fail";
  return { name: "Noise (σ in flat areas)", value: sigma, unit: "au", verdict };
}

function scoreFromKPIs(kpis: KPIResult[]): number {
  const weights: Record<string, number> = {
    "Sharpness (Var(Laplacian))": 0.25,
    "Edge density (>)": 0.1,
    "Blur likelihood": 0.2,
    "Focus @ center (ratio)": 0.15,
    "Exposure (mean Y)": 0.1,
    "Clipping (dark)": 0.05,
    "Clipping (bright)": 0.05,
    "Contrast (RMS Y)": 0.05,
    "Noise (σ in flat areas)": 0.05,
  };
  let s = 0, w = 0;
  for (const k of kpis) {
    const wt = weights[k.name] ?? 0.15;
    w += wt;
    const sub = k.verdict === "pass" ? 1 : k.verdict === "warn" ? 0.6 : 0.2;
    s += wt * sub;
  }
  return Math.round(100 * (s / Math.max(1e-6, w)));
}

function summarize(score: number): QualityReport["summary"] {
  if (score >= 80) return "good";
  if (score >= 60) return "ok";
  return "bad";
}

export async function analyzeImageQuality(file: File): Promise<QualityReport> {
  const { img, width, height } = await drawImageToCanvas(file);
  const { Y } = toLuma(img);

  // Compute independent KPIs in parallel
  const [sharp, edges, focus, exposureResults, contrast, noise] = await Promise.all([
    Promise.resolve(kpiSharpnessVarianceOfLaplacian(Y, width, height)),
    Promise.resolve(kpiEdgeDensity(Y, width, height)),
    Promise.resolve(kpiFocusAtCenter(Y, width, height)),
    Promise.resolve(kpiExposure(Y)),
    Promise.resolve(kpiContrast(Y)),
    Promise.resolve(kpiNoise(Y, width, height)),
  ]);

  // Blur depends on sharp, so compute it after
  const blur = kpiBlurLikelihood(sharp.value);
  const [exp, clipDark, clipBright] = exposureResults;
  
  const kpis: KPIResult[] = [sharp, edges, blur, focus, exp, clipDark, clipBright, contrast, noise];
  const score = scoreFromKPIs(kpis);
  const summary = summarize(score);
  
  URL.revokeObjectURL(URL.createObjectURL(file)); // Clean up
  
  return { width, height, kpis, score, summary };
}